import Keycloak from 'keycloak-js';
import { KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT_ID } from '@shared/constants/config';
import { ROUTES } from '@shared/constants/routes';
import { buildMainDomainUrl } from '@shared/lib/tenant';
import { markLoggedOut, withLoginPrompt } from '@shared/lib/loggedOutMarker';

let keycloakInstance = null;
let initPromise = null;
// Set once a sign-in or logout redirect has started. Several paths can fire for the same expired
// session (the refresh tick, Keycloak's onAuthLogout, a 401 the interceptor cannot refresh); only the
// first one redirects. Never reset — the page is navigating away.
let redirectInProgress = false;

export const initKeycloak = () => {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak({
      url: KEYCLOAK_URL,
      realm: KEYCLOAK_REALM,
      clientId: KEYCLOAK_CLIENT_ID,
    });
  }
  return keycloakInstance;
};

/**
 * Initialize the shared Keycloak instance, at most once per page load.
 *
 * keycloak-js throws "A 'Keycloak' instance can only be initialized once." on a second `init()`,
 * so the PROMISE is memoized, not just the instance — `initKeycloak()` alone is not enough.
 * Concurrent callers (notably React StrictMode's mount -> cleanup -> mount in development) all
 * await the same in-flight init and observe the same result. Same idea as the `inflightRequest`
 * guard in `shared/store/sidebarVisibilityStore.js`.
 *
 * `options` is honoured on the first call only; later calls return the original promise.
 * A rejection is cached too, so every caller sees the same failure rather than one of them
 * re-entering `init()` and hitting the throw above.
 *
 * @param {object} options Keycloak `init` options.
 * @returns {Promise<boolean>} Whether the user is authenticated.
 */
export const initKeycloakAuth = (options) => {
  initPromise ??= initKeycloak().init(options);
  return initPromise;
};

export const getKeycloak = () => {
  if (!keycloakInstance) {
    throw new Error('Keycloak not initialized. Call initKeycloak() first.');
  }
  return keycloakInstance;
};

/**
 * Trigger Keycloak login. Pass `{ idpHint }` on the subdomain path to pin the tenant's Identity
 * Provider (Keycloak jumps straight to it); omit it on the main domain for the generic login screen.
 * After a portal logout `prompt: 'login'` is added, see `withLoginPrompt`.
 * @param {{ idpHint?: string }} [options]
 */
export const login = (options = {}) => {
  const keycloak = getKeycloak();
  const loginOptions = options?.idpHint ? { idpHint: options.idpHint } : undefined;
  return keycloak.login(withLoginPrompt(loginOptions));
};

// The logout click waits for the revocation before redirecting, so a hung Keycloak must not hang it.
const REVOKE_TIMEOUT_MS = 5000;

/** URL of one of the realm's OpenID Connect endpoints, e.g. `oidcEndpoint('revoke')`. */
const oidcEndpoint = (path) => `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/${path}`;

/**
 * Revoke the portal's refresh token at Keycloak. Unlike `keycloak.logout()` (end_session), this ends
 * only the portal's client session: the realm session NumberCruncher Web shares stays alive.
 * Best effort — a failure or timeout is logged and the local logout continues.
 * @param {string|undefined} refreshToken
 */
const revokeRefreshToken = async (refreshToken) => {
  if (!refreshToken) return;
  try {
    const response = await globalThis.fetch(oidcEndpoint('revoke'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: KEYCLOAK_CLIENT_ID,
        token: refreshToken,
        token_type_hint: 'refresh_token',
      }),
      signal: globalThis.AbortSignal?.timeout?.(REVOKE_TIMEOUT_MS),
    });
    if (!response.ok) {
      console.warn('[Logout] Keycloak token revocation failed with status', response.status);
    }
  } catch (error) {
    console.warn('[Logout] Keycloak token revocation failed:', error);
  }
};

/**
 * Log out of the portal and land on the public `/logout` page (the "signed out" confirmation with
 * the Re Login button). Single source of truth for the post-logout redirect — both logout paths
 * (`useAuth().logout` for the deliberate logout, `useTenantBootstrap.logoutUser` for the forced
 * ones) call this, so the target can never drift between them.
 *
 * By default only the portal's own session ends: its refresh token is revoked and its tokens are
 * cleared, but the Keycloak realm session stays alive, because NumberCruncher Web (launched from
 * the portal) shares it and must stay signed in. The logged-out marker makes the next sign-in ask
 * for credentials. `{ global: true }` (account deletion, password change) instead ends the whole
 * Keycloak session through end_session, signing the user out of every application.
 *
 * The redirect always targets the MAIN portal host, never the current origin: logging out from a
 * company subdomain (acme.<base>) must not land back on that subdomain, or the bootstrap re-pins the
 * tenant IdP and re-enters the same company, so the user could never reach the company picker. On
 * the main domain this resolves to the same origin, so it is a no-op there.
 *
 * A global logout without an id token (keycloak-js cleared its tokens after a refresh was refused)
 * navigates straight to the logout page: Keycloak answers a logout without `id_token_hint` with its
 * own "Do you want to log out?" page, and there is nothing left to end there anyway.
 *
 * @param {import('keycloak-js').default|null} keycloakInstance live instance, or null when
 *   Keycloak never initialized — in which case we navigate directly.
 * @param {{ global?: boolean }} [options]
 */
export const logoutToLogoutPage = async (keycloakInstance, { global = false } = {}) => {
  if (redirectInProgress) return;
  redirectInProgress = true;

  const postLogoutUrl = buildMainDomainUrl(ROUTES.LOGOUT);
  markLoggedOut();

  if (global && keycloakInstance?.idToken) {
    try {
      await keycloakInstance.logout({ redirectUri: postLogoutUrl });
      return;
    } catch (error) {
      console.warn('[Logout] Keycloak logout failed, forcing redirect:', error);
    }
  } else if (keycloakInstance) {
    await revokeRefreshToken(keycloakInstance.refreshToken);
    // clearToken fires onAuthLogout, which useTenantBootstrap wires to the session-expired handler
    // (a second local cleanup and backend-cookie call). This logout redirects itself, so detach it.
    keycloakInstance.onAuthLogout = undefined;
    keycloakInstance.clearToken?.();
  }

  globalThis.location.href = postLogoutUrl;
};

/**
 * The session expired mid-use: send the user straight to the Keycloak sign-in page and bring them
 * back to the page they were on. Unlike logoutToLogoutPage this stays on the current host, so on a
 * company subdomain `idpHint` pins that tenant's Identity Provider again.
 *
 * Shares the one-redirect guard with logoutToLogoutPage, so when the paths race (onAuthLogout fires
 * before the HTTP client's 401 handler gives up) only the first redirect happens.
 *
 * Falls back to the main portal root when Keycloak never initialized or `login()` fails — the app
 * then starts up unauthenticated and ProtectedRoute sends the user to sign-in.
 *
 * @param {import('keycloak-js').default|null} keycloakInstance
 * @param {{ idpHint?: string|null }} [options]
 */
export const redirectToLogin = async (keycloakInstance, { idpHint } = {}) => {
  if (redirectInProgress) return;
  redirectInProgress = true;

  const fallbackUrl = buildMainDomainUrl('/');

  if (!keycloakInstance) {
    globalThis.location.href = fallbackUrl;
    return;
  }

  try {
    await keycloakInstance.login(withLoginPrompt({
      ...(idpHint ? { idpHint } : {}),
      redirectUri: globalThis.location.href,
    }));
  } catch (error) {
    console.warn('[Session] Keycloak login redirect failed, forcing redirect:', error);
    globalThis.location.href = fallbackUrl;
  }
};

export const getToken = () => {
  const keycloak = getKeycloak();
  return keycloak.token;
};

export const getRefreshToken = () => {
  const keycloak = getKeycloak();
  return keycloak.refreshToken;
};

export const updateToken = (minValidity = 5) => {
  const keycloak = getKeycloak();
  return keycloak.updateToken(minValidity);
};

export const isAuthenticated = () => {
  const keycloak = getKeycloak();
  return keycloak.authenticated;
};

export const getUserInfo = () => {
  const keycloak = getKeycloak();
  if (!keycloak.tokenParsed) {
    return null;
  }
  
  return {
    id: keycloak.tokenParsed.sub,
    email: keycloak.tokenParsed.email,
    firstName: keycloak.tokenParsed.given_name || '',
    lastName: keycloak.tokenParsed.family_name || '',
    fullName: keycloak.tokenParsed.name || '',
    username: keycloak.tokenParsed.preferred_username || '',
    roles: keycloak.tokenParsed.realm_access?.roles || [],
  };
};
